import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react'

import { forgetRun, personalProject } from '@shared/services/pictureService'
import { copyProjectFiles, listProjectFiles, uploadProjectFile } from '@shared/services/projectFileService'
import { getSkillService } from '@shared/services/skillService'
import type { CanvasDocument } from '@shared/services/canvasService'
import { sendToMiroQueue } from '@shared/services/miroQueue'
import {
  awaitUpscale,
  estimateUpscale,
  listUpscales,
  startUpscale,
  upscaleOptions,
  type UpscaleHistory,
  type UpscaleOptions,
  type UpscaleResult,
  type UpscaledPicture,
} from '@shared/services/upscaleService'
import { UpscaleControls, usableSize, type UpscaleChoice } from '@shared/components/UpscaleControls'
import { CompareSlider } from '@shared/components/CompareSlider'
import { WwLoaderFill } from '@shared/components/WwLoader'
import { useRemembered } from '../useRemembered'
import { putClip } from '@shared/services/projectClip'
import { createConversation } from '@shared/services/chatService'
import { saveCanvas } from '@shared/services/canvasService'
import { openProjectLater } from '../openProject'
import { HoldButton } from '../components/HoldButton'
import '../components/pictureSources.css'
import './upscalePage.css'

/**
 * Upscale, as a page
 * -------------------------------------------------------------
 * The settings on the left, all of them in view without scrolling; down the
 * right, one picture per row, as large as the column allows, with a line to
 * drag across it -- the original to the left of the line, the upscaled picture
 * to the right -- and beside it what it was made with, as the generate page
 * keeps a prompt beside a picture (Huanrui, 2026-10-05).
 *
 * Kept in the same hidden project as the generate page, so a picture upscaled
 * in the Miro panel is listed here too. The originals are never changed.
 */

interface Chosen {
  /** The stored file's id, or `local:<n>` while it is still uploading. */
  id: string
  url: string
  title: string
  width: number
  height: number
  pending?: boolean
}

/** Uploads at once. */
const AT_ONCE = 3

let localCount = 0

async function measure(file: File): Promise<{ width: number; height: number } | undefined> {
  try {
    const bitmap = await createImageBitmap(file)
    const size = { width: bitmap.width, height: bitmap.height }
    bitmap.close()
    return size
  } catch {
    return undefined
  }
}

/** Save a picture to disk; a tab if the browser will not. */
async function download(picture: UpscaledPicture): Promise<void> {
  try {
    const response = await fetch(picture.originalPath)
    if (!response.ok) throw new Error(String(response.status))
    const blob = await response.blob()
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    const extension = blob.type === 'image/jpeg' ? 'jpg' : blob.type === 'image/webp' ? 'webp' : 'png'
    const base = (picture.title || 'upscaled').replace(/[\\/:*?"<>|]+/g, ' ').trim() || 'upscaled'
    link.download = `${base}.${extension}`
    document.body.append(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(link.href), 10_000)
  } catch {
    window.open(picture.originalPath, '_blank', 'noopener')
  }
}

/** One row: a picture that went in, what it became, and the run it was part of. */
interface Row {
  run: UpscaleHistory
  result: UpscaleResult
  index: number
}

export function UpscalePage() {
  const [project, setProject] = useState('')
  const [options, setOptions] = useState<UpscaleOptions | null>(null)
  const [history, setHistory] = useState<UpscaleHistory[]>([])
  const [error, setError] = useState('')
  const [chosen, setChosen] = useState<Chosen[]>([])
  const [dragging, setDragging] = useState(false)
  const [going, setGoing] = useState(0)
  const [live, setLive] = useState({ live: 0, limit: 5 })
  const upload = useRef<HTMLInputElement | null>(null)
  /** The column of results, scrolled back to the newest when a run starts. */
  const list = useRef<HTMLElement | null>(null)
  // Remembered with the account, like the other pages' settings.
  const [choice, setChoice] = useRemembered<UpscaleChoice>('ww.upscale.choice', {
    model: '',
    size: '2k',
    face: false,
  })

  /**
   * The first link each picture was given, kept for the visit. Every answer
   * signs the pictures afresh, and a new link for the same picture is a new
   * download of tens of megabytes and a flash on screen -- on every poll.
   */
  const links = useRef(new Map<string, UpscaledPicture>())
  const steady = useCallback((runs: UpscaleHistory[]): UpscaleHistory[] => {
    for (const run of runs) {
      for (const [name, picture] of run.pictures) {
        const kept = links.current.get(name)
        if (kept) run.pictures.set(name, kept)
        else links.current.set(name, picture)
      }
    }
    return runs
  }, [])

  const refresh = useCallback(
    async (id: string) => {
      try {
        setHistory(steady(await listUpscales(id)))
      } catch (err) {
        setError(err instanceof Error ? err.message : 'The list could not be read')
      }
    },
    [steady],
  )

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const [id, found] = await Promise.all([personalProject(), upscaleOptions()])
        if (cancelled) return
        setProject(id)
        setOptions(found)
        void refresh(id)
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'This could not load')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [refresh])

  const ready = chosen.filter((one) => !one.pending)
  const dims = useMemo(() => chosen.map((one) => ({ width: one.width, height: one.height })), [chosen])
  const model =
    options?.models.find((one) => one.key === choice.model) ??
    options?.models.find((one) => one.key === options.defaultModel) ??
    options?.models[0]
  const sizeValue = options ? usableSize(options, choice.size, dims, model?.key) : choice.size
  const size = options?.sizes.find((one) => one.value === sizeValue)
  const estimate = options && model && size ? estimateUpscale(options, model, size, dims) : 0
  const full = live.live >= live.limit
  const tooMany = options ? chosen.length > options.maxPictures : false

  /* ---------- pictures in ---------- */

  const add = useCallback(
    async (files: File[]) => {
      if (!project) return
      const pictures = files.filter((file) => file.type.startsWith('image/'))
      if (pictures.length === 0) return
      setError('')
      const entries = await Promise.all(
        pictures.map(async (file) => {
          const size = await measure(file)
          return {
            file,
            size,
            chosen: {
              id: `local:${(localCount += 1)}`,
              url: URL.createObjectURL(file),
              title: file.name,
              width: size?.width ?? 0,
              height: size?.height ?? 0,
              pending: true,
            } satisfies Chosen,
          }
        }),
      )
      setChosen((prev) => [...prev, ...entries.map((one) => one.chosen)])

      let next = 0
      const failed: string[] = []
      const worker = async () => {
        while (next < entries.length) {
          const entry = entries[next++]!
          try {
            const stored = await uploadProjectFile(project, entry.file, entry.size)
            setChosen((prev) => {
              if (!prev.some((one) => one.id === entry.chosen.id)) return prev
              if (prev.some((one) => one.id === stored.id)) return prev.filter((one) => one.id !== entry.chosen.id)
              return prev.map((one) =>
                one.id === entry.chosen.id
                  ? {
                      ...one,
                      id: stored.id,
                      width: one.width || stored.width || 0,
                      height: one.height || stored.height || 0,
                      pending: false,
                    }
                  : one,
              )
            })
          } catch (err) {
            failed.push(err instanceof Error ? err.message : 'could not be uploaded')
            setChosen((prev) => prev.filter((one) => one.id !== entry.chosen.id))
          }
        }
      }
      await Promise.all(Array.from({ length: Math.min(AT_ONCE, entries.length) }, worker))
      if (failed.length > 0) setError(`${failed.length} could not be uploaded: ${failed[0]}`)
    },
    [project],
  )

  // Ctrl+V anywhere on the page, as on the generate page.
  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      const files = [...(event.clipboardData?.files ?? [])].filter((file) => file.type.startsWith('image/'))
      if (files.length === 0) return
      event.preventDefault()
      void add(files)
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
  }, [add])

  const fromClipboard = useCallback(async () => {
    try {
      const items = await navigator.clipboard.read()
      const files: File[] = []
      for (const item of items) {
        const type = item.types.find((one) => one.startsWith('image/'))
        if (!type) continue
        const blob = await item.getType(type)
        files.push(new File([blob], `pasted.${type.split('/')[1] || 'png'}`, { type }))
      }
      if (files.length === 0) {
        setError('There is no picture on the clipboard')
        return
      }
      await add(files)
    } catch {
      setError('The browser would not hand over the clipboard — press Ctrl+V instead')
    }
  }, [add])

  const onDrop = (event: DragEvent) => {
    event.preventDefault()
    setDragging(false)
    void add([...event.dataTransfer.files])
  }

  /* ---------- running ---------- */

  const run = useCallback(async () => {
    if (!project || !model || !size || ready.length === 0 || full || tooMany) return
    setError('')
    setGoing((was) => was + 1)
    const inputs = ready.map((one) => ({ store: 'project' as const, id: one.id }))
    try {
      const began = await startUpscale({
        projectId: project,
        inputs,
        model: model.key,
        size: size.value,
        face: model.face && choice.face,
      })
      setLive({ live: began.live, limit: began.limit })
      setChosen([])
      await refresh(project)
      list.current?.scrollTo({ top: 0, behavior: 'smooth' })
      // The list again whenever another picture is finished, so each one
      // shows as soon as it exists rather than all at the end.
      let made = 0
      const done = await awaitUpscale(began.runId, (progress) => {
        const now = progress.results.filter((one) => one.output || one.error).length
        if (now !== made) {
          made = now
          void refresh(project)
        }
      })
      if (done.limit) setLive({ live: done.live, limit: done.limit })
      if (done.status !== 'succeeded') setError(done.error || 'It did not finish')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'It did not start')
    } finally {
      setGoing((was) => Math.max(0, was - 1))
      await refresh(project)
      // A 4K picture is tens of megabytes: this has the browser make the
      // smaller copy the list draws, once (projectFileService, drawing copies).
      void listProjectFiles(project, { thumbs: true }).catch(() => {})
    }
  }, [choice.face, full, model, project, ready, refresh, size, tooMany])

  /* ---------- after ---------- */

  const forget = useCallback(async (runId: string) => {
    const sure = window.confirm(
      'Delete this upscale and the pictures it made? They go for good. The originals are kept.',
    )
    if (!sure) return
    try {
      await forgetRun(runId)
      setHistory((prev) => prev.filter((one) => one.id !== runId))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'It could not be deleted')
    }
  }, [])

  const [starting, setStarting] = useState('')
  /**
   * A board with the original, an Upscale card set as this one was, and the
   * upscaled picture, wired up -- what Create a project does on the generate
   * page. The pictures are copied into the new project first: a board reads
   * only its own project's files.
   */
  /**
   * Copy to Project (Huanrui, 2026-10-07), as on the generate page: the
   * original, an Upscale card set as this one was, and the upscaled picture,
   * wired, offered to whichever project is opened next (projectClip.ts). It
   * used to make a new project every time.
   */
  const [copiedKey, setCopiedKey] = useState('')

  /**
   * Copy to Project, held (Huanrui, 2026-10-07): a new project with the
   * original, the card and the upscaled picture on its board, opened in a
   * new tab -- what "Create a project" used to do.
   */
  const makeProject = useCallback(
    async (row: Row, after: UpscaledPicture) => {
      if (starting) return
      setStarting(`${row.run.id}-${row.index}`)
      const tab = openProjectLater()
      try {
        const made = await createConversation()
        const before = row.run.pictures.get(row.result.input)
        const ids = [before, after]
          .filter((one): one is UpscaledPicture => Boolean(one) && one!.store === 'project')
          .map((one) => one.id)
        const [copied, skill] = await Promise.all([
          copyProjectFiles(made.id, ids),
          getSkillService()
            .getSkill('upscale')
            .catch(() => null),
        ])
        await saveCanvas(made.id, boardFor(row, before, after, copied, skill, options))
        tab.go(`/projects/${made.id}`)
      } catch (err) {
        tab.cancel()
        setError(err instanceof Error ? err.message : 'A project could not be made')
      } finally {
        setStarting('')
      }
    },
    [options, starting],
  )
  const copyToProject = useCallback(
    async (row: Row, after: UpscaledPicture) => {
      if (starting) return
      const key = `${row.run.id}-${row.index}`
      setStarting(key)
      try {
        const before = row.run.pictures.get(row.result.input)
        const skill = await getSkillService()
          .getSkill('upscale')
          .catch(() => null)
        // The ids are this person's own for now; the board swaps them for its copies.
        const doc = boardFor(row, before, after, {}, skill, options)
        const shown = [before, after].filter((one): one is UpscaledPicture => Boolean(one))
        putClip(
          'Upscale',
          shown.map((one) => ({ kind: 'file' as const, id: one.id, thumb: one.thumbnailPath || one.originalPath, title: one.title })),
          { nodes: doc.nodes, edges: doc.edges, files: shown.filter((one) => one.store === 'project').map((one) => one.id) },
        )
        setCopiedKey(key)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'It could not be copied')
      } finally {
        setStarting('')
      }
    },
    [options, starting],
  )

  /* ---------- drawing ---------- */

  const modelOf = (key: string) => options?.models.find((one) => one.key === key)
  const rows: Row[] = history.flatMap((run) => run.results.map((result, index) => ({ run, result, index })))

  return (
    <div className="ups">
      <section
        className={`ups__make${dragging ? ' ups__make--over' : ''}`}
        onDragOver={(event) => {
          event.preventDefault()
          setDragging(true)
        }}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as globalThis.Node | null)) setDragging(false)
        }}
        onDrop={onDrop}
      >
        <div className="ups__field">
          <span className="ups__label">
            Pictures
            {chosen.length > 0 ? <em> · {chosen.length}</em> : null}
          </span>
          {/* The Edit Text page's two tiles (Huanrui, 2026-10-07). */}
          <div className="psrc">
            <button type="button" className="psrc__tile" onClick={() => upload.current?.click()}>
              <span className="psrc__plus" aria-hidden="true">+</span>
              <strong>This computer</strong>
              <em>or drop pictures here</em>
            </button>
            <button type="button" className="psrc__tile" onClick={() => void fromClipboard()}>
              <span className="psrc__plus" aria-hidden="true">+</span>
              <strong>Clipboard</strong>
              <em>or press Ctrl+V</em>
            </button>
          </div>
          <input
            ref={upload}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            multiple
            hidden
            onChange={(event) => {
              const files = [...(event.target.files ?? [])]
              event.target.value = ''
              void add(files)
            }}
          />
          {chosen.length > 0 ? (
            <div className="ups__chosen">
              {chosen.map((one) => (
                <span
                  key={one.id}
                  className={`ups__pick${one.pending ? ' ups__pick--pending' : ''}`}
                  title={one.width ? `${one.title} · ${one.width} × ${one.height}` : one.title}
                >
                  <img src={one.url} alt="" />
                  {one.width ? <em>{Math.max(one.width, one.height)}</em> : null}
                  <button
                    type="button"
                    aria-label="Take this picture out"
                    title="Take this picture out"
                    onClick={() => setChosen((prev) => prev.filter((other) => other.id !== one.id))}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          ) : null}
        </div>

        {options ? (
          <UpscaleControls
            compact
            fill
            options={options}
            value={{ model: model?.key ?? '', size: sizeValue, face: choice.face }}
            pictures={dims}
            onChange={(patch) => setChoice({ ...choice, ...patch })}
          />
        ) : null}

        <div className="ups__end">
          {options && !options.ready ? <p className="ups__error">Upscale is not switched on yet.</p> : null}
          {tooMany && options ? <p className="ups__error">{options.maxPictures} at a time is the most.</p> : null}
          {error ? <p className="ups__error">{error}</p> : null}
          <button
            type="button"
            className="ups__run"
            disabled={!options?.ready || !model || ready.length === 0 || chosen.some((one) => one.pending) || full || tooMany}
            onClick={() => void run()}
          >
            {full ? (
              `Queued — ${live.live} already going`
            ) : (
              <>
                Upscale{ready.length > 1 ? ` ${ready.length} pictures` : ''}
                {estimate > 0 ? <span className="ups__credits">about {estimate} credits</span> : null}
              </>
            )}
          </button>
          {going > 0 ? (
            <p className="ups__going">
              {going} running · up to {live.limit} at once
            </p>
          ) : null}
        </div>
      </section>

      <section className="ups__history" ref={list}>
        {rows.length === 0 ? <p className="ups__empty">Everything you upscale will appear down this side.</p> : null}
        {rows.map((row) => (
          <UpscaleRow
            key={`${row.run.id}-${row.index}`}
            row={row}
            modelLabel={modelOf(row.run.model)?.label ?? row.run.model}
            faceOffered={modelOf(row.run.model)?.face ?? false}
            perCredit={(options?.usdPerCredit ?? 0) * 1000}
            starting={starting === `${row.run.id}-${row.index}`}
            onAgain={() => setChoice({ model: row.run.model, size: row.run.size, face: row.run.face })}
            onProject={(after) => void copyToProject(row, after)}
            onNewProject={(after) => void makeProject(row, after)}
            copied={copiedKey === `${row.run.id}-${row.index}`}
            onForget={() => void forget(row.run.id)}
          />
        ))}
      </section>
    </div>
  )
}

/* ---------- one row ---------- */

function UpscaleRow({
  row,
  modelLabel,
  faceOffered,
  perCredit,
  starting,
  onAgain,
  onProject,
  onNewProject,
  copied,
  onForget,
}: {
  row: Row
  modelLabel: string
  faceOffered: boolean
  /** This platform's credits per Topaz credit. */
  perCredit: number
  starting: boolean
  onAgain: () => void
  onProject: (after: UpscaledPicture) => void
  /** Copy to Project held: a new project made with it. */
  onNewProject: (after: UpscaledPicture) => void
  /** Copied to Project, and waiting for a project to take it. */
  copied: boolean
  onForget: () => void
}) {
  const { run, result, index } = row
  const before = run.pictures.get(result.input)
  const after = result.output ? run.pictures.get(result.output) : undefined
  const working = !result.output && !result.error && (run.status === 'queued' || run.status === 'running')
  const ratio =
    (after?.width && after.height ? after.width / after.height : 0) ||
    (before?.width && before.height ? before.width / before.height : 0) ||
    16 / 9
  const credits =
    result.credits && perCredit > 0 ? Math.round(result.credits * perCredit) : index === 0 ? run.credits : 0
  const [sent, setSent] = useState<'' | 'sending' | 'sent' | 'failed'>('')

  const toMiro = async () => {
    if (!after || sent === 'sending') return
    setSent('sending')
    try {
      // One picture: the panel puts it straight on the board, full size
      // (boardQueue.ts, placeSingle).
      await sendToMiroQueue('web', [{ store: after.store, id: after.id }])
      setSent('sent')
    } catch {
      setSent('failed')
    }
  }

  const grew = `${before?.width ? `${before.width} × ${before.height}` : 'the original'} became ${after?.width || result.width} × ${after?.height || result.height}.`
  const said = after
    ? result.capped
      ? `Upscaled with ${modelLabel}, which enlarges at most 6×: ${grew} ${run.size.toUpperCase()} would have needed more.`
      : `Upscaled with ${modelLabel} to ${run.size.toUpperCase()}: ${grew}`
    : working
      ? `Upscaling with ${modelLabel} to ${run.size.toUpperCase()}…`
      : result.error || run.error || 'It did not finish'

  return (
    <article className="ups__row">
      <div className="ups__view">
        {after && before ? (
          <div className="ups__shot">
            <CompareSlider
              before={before.originalPath}
              after={after.originalPath}
              ratio={ratio}
              afterLabel="Enhanced"
            />
            <button
              type="button"
              className="ups__save"
              title="Save it"
              aria-label="Save it"
              onPointerDown={(event) => event.stopPropagation()}
              onClick={() => void download(after)}
            >
              <Arrow />
            </button>
          </div>
        ) : (
          <div
            className={`ups__frame${working ? '' : ' ups__frame--failed'}`}
            style={{ aspectRatio: String(ratio), ['--wwcmp-ratio' as string]: String(ratio) }}
          >
            {before ? <img src={before.thumbnailPath} alt="" /> : null}
            {working ? <WwLoaderFill label="Upscaling" size="large" /> : null}
          </div>
        )}
      </div>

      {/* What it was made with, beside it, as the generate page keeps a
          prompt beside a picture. */}
      <aside className="ups__about">
        <p className={`ups__said${!after && !working ? ' ups__said--failed' : ''}`}>{said}</p>
        <span className="ups__tags">
          <em>{modelLabel}</em>
          <em>{run.size.toUpperCase()}</em>
          {faceOffered ? <em>{run.face ? 'Faces improved' : 'Faces as they were'}</em> : null}
          {credits ? <em>{credits} credits</em> : null}
        </span>
        <button type="button" className="ups__again" onClick={onAgain} title="Use these settings for the next one">
          Use these settings
        </button>
        {/* At the foot of the column, the same size, as Huanrui laid them
            out (2026-10-05). */}
        {after ? (
          <div className="ups__acts">
            {sent === 'sent' ? <p className="ups__note">Sent. Open the WonderWorks panel in Miro to place it.</p> : null}
            {sent === 'failed' ? <p className="ups__error">It could not be sent to Miro.</p> : null}
            {copied ? <p className="ups__note">Copied. Open any project and it will be offered there, with its card.</p> : null}
            <HoldButton
              className="ups__act"
              onClick={() => onProject(after)}
              onHold={() => onNewProject(after)}
              disabled={starting}
            >
              {starting ? 'Working…' : 'Copy to Project'}
            </HoldButton>
            <button type="button" className="ups__act" onClick={() => void toMiro()} disabled={sent === 'sending'}>
              {sent === 'sending' ? 'Sending…' : 'Send to Miro'}
            </button>
          </div>
        ) : null}
        {!working ? (
          <button
            type="button"
            className="ups__bin"
            onClick={onForget}
            title="Delete this upscale and the pictures it made. The originals stay."
            aria-label="Delete this upscale and the pictures it made"
          >
            <Bin />
          </button>
        ) : null}
      </aside>
    </article>
  )
}

/* ---------- a board made from one row ---------- */

/** As the generate page lays a run out: what went in, the card, what came out. */
function boardFor(
  row: Row,
  before: UpscaledPicture | undefined,
  after: UpscaledPicture,
  copied: Record<string, string>,
  skill: { id: string; name: string; glyph: string } | null,
  options: UpscaleOptions | null,
): CanvasDocument {
  const page = { id: 'page-1', name: 'Page 1' }
  const width = 320
  const heightOf = (one: UpscaledPicture | undefined) =>
    one?.width && one.height ? Math.round((width * one.height) / one.width) : 200
  const pointAt = (one: UpscaledPicture) =>
    one.store === 'library' ? { assetId: one.id } : { fileId: copied[one.id] ?? one.id }
  const nodes: CanvasDocument['nodes'] = []
  const edges: CanvasDocument['edges'] = []
  const model = options?.models.find((one) => one.key === row.run.model)

  if (before) {
    nodes.push({
      id: 'in-1',
      type: 'image',
      page: page.id,
      position: { x: 0, y: 0 },
      width,
      height: heightOf(before),
      data: { ...pointAt(before), title: before.title || 'Original' },
    })
    edges.push({ id: 'w-in-1', source: 'in-1', target: 'card-1', targetHandle: 'in' })
  }
  nodes.push({
    id: 'card-1',
    type: 'upscale',
    page: page.id,
    position: { x: 440, y: 0 },
    data: {
      ...(skill ? { skillRef: skill.id, name: skill.name, glyph: skill.glyph } : {}),
      model: row.run.model,
      size: row.run.size,
      face: row.run.face,
      open: false,
    },
  })
  nodes.push({
    id: 'out-1',
    type: 'image',
    page: page.id,
    position: { x: 760, y: 0 },
    width,
    height: heightOf(after),
    data: {
      ...pointAt(after),
      title: after.title || 'Upscaled',
      made: {
        prompt: '',
        model: row.run.model,
        size: row.run.size.toUpperCase(),
        quality: '',
        at: row.run.createdAt,
        upscale: {
          face: model?.face ? row.run.face : null,
          pixels: after.width ? `${after.width} × ${after.height}` : '',
        },
      },
    },
  })
  edges.push({ id: 'w-out-1', source: 'card-1', target: 'out-1' })
  return { pages: [page], nodes, edges }
}

function Arrow() {
  return (
    <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
      <path d="M8 2.6v7.2m0 0 2.8-2.8M8 9.8 5.2 7" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3.2 12.6h9.6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function Bin() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <path d="M3 4.5h10M6.5 4.5V3h3v1.5M4.5 4.5l.6 8.5h5.8l.6-8.5" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
